import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Note, NoteComment, NoteType, NotePriority } from '../utils/types';

// مجموعات Firestore
const NOTES_COLLECTION = 'notes';
const NOTE_COMMENTS_COLLECTION = 'noteComments';

// ===============================
// خدمات الملاحظات (Notes)
// ===============================

/**
 * إنشاء ملاحظة جديدة
 */
export const createNote = async (noteData: Omit<Note, 'id' | 'createdAt' | 'updatedAt' | 'comments'>): Promise<string> => {
  try {
    // تنظيف البيانات من القيم undefined
    const cleanedData: any = {
      title: noteData.title,
      content: noteData.content,
      authorId: noteData.authorId,
      authorName: noteData.authorName,
      type: noteData.type,
      priority: noteData.priority,
      tags: noteData.tags || [],
      sharedWith: noteData.sharedWith || [],
      isCompleted: noteData.isCompleted || false,
      isArchived: noteData.isArchived || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      comments: [],
    };

    // إضافة المرفقات فقط إذا كانت موجودة
    if (noteData.attachments && noteData.attachments.length > 0) {
      cleanedData.attachments = noteData.attachments;
    }

    // إضافة تاريخ الاستحقاق فقط إذا كان موجوداً
    if (noteData.dueDate) {
      cleanedData.dueDate = noteData.dueDate;
    }

    // إضافة معلومات الإكمال فقط إذا كانت موجودة
    if (noteData.completedAt) {
      cleanedData.completedAt = noteData.completedAt;
    }

    if (noteData.completedBy) {
      cleanedData.completedBy = noteData.completedBy;
    }

    // إضافة معلومات الأرشفة فقط إذا كانت موجودة
    if (noteData.archivedAt) {
      cleanedData.archivedAt = noteData.archivedAt;
    }

    const docRef = await addDoc(collection(db, NOTES_COLLECTION), cleanedData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating note:', error);
    throw error;
  }
};

/**
 * الحصول على جميع الملاحظات للمستخدم
 */
export const getUserNotes = async (userId: string): Promise<Note[]> => {
  try {
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('authorId', '==', userId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const notes: Note[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note);
    });

    return notes;
  } catch (error) {
    console.error('Error getting user notes:', error);
    throw error;
  }
};

/**
 * الحصول على الملاحظات المشتركة مع المستخدم
 */
export const getSharedNotes = async (userId: string): Promise<Note[]> => {
  try {
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('sharedWith', 'array-contains', userId),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const notes: Note[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note);
    });

    return notes;
  } catch (error) {
    console.error('Error getting shared notes:', error);
    throw error;
  }
};

/**
 * الحصول على الإعلانات العامة
 */
export const getAnnouncementNotes = async (): Promise<Note[]> => {
  try {
    const q = query(
      collection(db, NOTES_COLLECTION),
      where('type', '==', 'announcement'),
      where('isArchived', '==', false),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const querySnapshot = await getDocs(q);
    const notes: Note[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note);
    });

    return notes;
  } catch (error) {
    console.error('Error getting announcement notes:', error);
    throw error;
  }
};

/**
 * الحصول على ملاحظة بالمعرف
 */
export const getNoteById = async (noteId: string): Promise<Note | null> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note;
    }

    return null;
  } catch (error) {
    console.error('Error getting note:', error);
    throw error;
  }
};

/**
 * تحديث ملاحظة
 */
export const updateNote = async (noteId: string, updates: Partial<Note>): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating note:', error);
    throw error;
  }
};

/**
 * حذف ملاحظة
 */
export const deleteNote = async (noteId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting note:', error);
    throw error;
  }
};

/**
 * أرشفة ملاحظة
 */
export const archiveNote = async (noteId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      isArchived: true,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error archiving note:', error);
    throw error;
  }
};

/**
 * إلغاء أرشفة ملاحظة
 */
export const unarchiveNote = async (noteId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      isArchived: false,
      archivedAt: null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error unarchiving note:', error);
    throw error;
  }
};

/**
 * تمييز ملاحظة كمكتملة
 */
export const completeNote = async (noteId: string, userId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      isCompleted: true,
      completedAt: serverTimestamp(),
      completedBy: userId,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error completing note:', error);
    throw error;
  }
};

/**
 * إلغاء تمييز ملاحظة كمكتملة
 */
export const uncompleteNote = async (noteId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      isCompleted: false,
      completedAt: null,
      completedBy: null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error uncompleting note:', error);
    throw error;
  }
};

/**
 * مشاركة ملاحظة مع مستخدمين
 */
export const shareNote = async (noteId: string, userIds: string[]): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      sharedWith: arrayUnion(...userIds),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error sharing note:', error);
    throw error;
  }
};

/**
 * إلغاء مشاركة ملاحظة مع مستخدمين
 */
export const unshareNote = async (noteId: string, userIds: string[]): Promise<void> => {
  try {
    const docRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(docRef, {
      sharedWith: arrayRemove(...userIds),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error unsharing note:', error);
    throw error;
  }
};

/**
 * البحث في الملاحظات
 */
export const searchNotes = async (userId: string, searchTerm: string): Promise<Note[]> => {
  try {
    // البحث في الملاحظات الشخصية
    const personalQuery = query(
      collection(db, NOTES_COLLECTION),
      where('authorId', '==', userId),
      where('isArchived', '==', false)
    );

    // البحث في الملاحظات المشتركة
    const sharedQuery = query(
      collection(db, NOTES_COLLECTION),
      where('sharedWith', 'array-contains', userId),
      where('isArchived', '==', false)
    );

    const [personalSnapshot, sharedSnapshot] = await Promise.all([
      getDocs(personalQuery),
      getDocs(sharedQuery)
    ]);

    const allNotes: Note[] = [];

    // معالجة الملاحظات الشخصية
    personalSnapshot.forEach((doc) => {
      const data = doc.data();
      allNotes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note);
    });

    // معالجة الملاحظات المشتركة
    sharedSnapshot.forEach((doc) => {
      const data = doc.data();
      // تجنب التكرار
      if (!allNotes.find(note => note.id === doc.id)) {
        allNotes.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          dueDate: data.dueDate?.toDate(),
          completedAt: data.completedAt?.toDate(),
          archivedAt: data.archivedAt?.toDate(),
        } as Note);
      }
    });

    // تصفية النتائج بناءً على مصطلح البحث
    const filteredNotes = allNotes.filter(note => 
      note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return filteredNotes.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  } catch (error) {
    console.error('Error searching notes:', error);
    throw error;
  }
};

/**
 * الاستماع للتغييرات في الملاحظات
 */
export const subscribeToUserNotes = (
  userId: string,
  callback: (notes: Note[]) => void
): (() => void) => {
  const q = query(
    collection(db, NOTES_COLLECTION),
    where('authorId', '==', userId),
    where('isArchived', '==', false),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const notes: Note[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        dueDate: data.dueDate?.toDate(),
        completedAt: data.completedAt?.toDate(),
        archivedAt: data.archivedAt?.toDate(),
      } as Note);
    });
    callback(notes);
  });
};

// ===============================
// خدمات تعليقات الملاحظات
// ===============================

/**
 * إضافة تعليق على ملاحظة
 */
export const addNoteComment = async (commentData: Omit<NoteComment, 'id' | 'createdAt'>): Promise<string> => {
  try {
    const newComment = {
      ...commentData,
      createdAt: serverTimestamp(),
      isEdited: false,
    };

    const docRef = await addDoc(collection(db, NOTE_COMMENTS_COLLECTION), newComment);

    // تحديث الملاحظة لإضافة التعليق
    const noteRef = doc(db, NOTES_COLLECTION, commentData.noteId);
    await updateDoc(noteRef, {
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding note comment:', error);
    throw error;
  }
};

/**
 * الحصول على تعليقات ملاحظة
 */
export const getNoteComments = async (noteId: string): Promise<NoteComment[]> => {
  try {
    const q = query(
      collection(db, NOTE_COMMENTS_COLLECTION),
      where('noteId', '==', noteId),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const comments: NoteComment[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      comments.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        editedAt: data.editedAt?.toDate(),
      } as NoteComment);
    });

    return comments;
  } catch (error) {
    console.error('Error getting note comments:', error);
    throw error;
  }
};

/**
 * تحديث تعليق
 */
export const updateNoteComment = async (commentId: string, content: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTE_COMMENTS_COLLECTION, commentId);
    await updateDoc(docRef, {
      content,
      isEdited: true,
      editedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating note comment:', error);
    throw error;
  }
};

/**
 * حذف تعليق
 */
export const deleteNoteComment = async (commentId: string): Promise<void> => {
  try {
    const docRef = doc(db, NOTE_COMMENTS_COLLECTION, commentId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting note comment:', error);
    throw error;
  }
};

/**
 * الاستماع للتغييرات في تعليقات الملاحظة
 */
export const subscribeToNoteComments = (
  noteId: string,
  callback: (comments: NoteComment[]) => void
): (() => void) => {
  const q = query(
    collection(db, NOTE_COMMENTS_COLLECTION),
    where('noteId', '==', noteId),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const comments: NoteComment[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      comments.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        editedAt: data.editedAt?.toDate(),
      } as NoteComment);
    });
    callback(comments);
  });
};
